/* ==========================================================================

 Title

 Author:     Stephen Zsolnai (http://www.zolla.co.uk)
 Decription: Initialisation for config, translations and globalisation data.

 All json dependencies are loaded async before the site can load properly.

 Once we have them they are saved to local storage.

 Saved to local storage and cache busted based on:
 Arrow.buildVersion for config
 Arrow.translationsCacheKey for translations

 setup urls and config stuff applied to global Arrow namespace in the page header

 ========================================================================== */

/*global Arrow */

'use strict';

var $ = require('jquery');
var _ = require('underscore');
var loglevel = require('loglevel');
var cookies = require('js-cookie');
var mediator = require('../js/mediator');
var utilities = require('../js/utilities');
var config = require('../js/config');
var session = require('../js/session/index');
var Analytics = require('../components/Analytics/Analytics');

import { initGlobalAjaxSettings } from './ajaxSetup';
import { runRequest } from './makeRequest';
import { EXPIRY_HANDLER_KEY } from './requestExpiryHandlers';

var initialisationItems = {};
const log = loglevel.getLogger('Initialisation');

export class Initialisation {
  constructor() {
    this.translationsCacheKey = Arrow.translationsCacheKey;
    this.buildVersion = Arrow.buildVersion;

    initGlobalAjaxSettings(Arrow.requestId);

    //Update session Model isLoggedIn
    session.sessionController.model.set({
      loggedIn: Arrow.isAuthorized,
      userDisplayName: Arrow.currentUserDisplayName
    });

    config.locale = {};

    // Get the 2 letter language code from the DOM
    if (document.documentElement.lang) {
      config.locale.code = document.documentElement.lang.split('-')[0];
    }

    config.locale.exchangeRate = Arrow.fromUSDConversionRate;
    if (!config.locale.exchangeRate) {
      log.error(new Error('Conversion rate not found'));
    }

    const utm = this.extractUtm(document.location.search);
    // only override the utm info when there are new info
    if (!_.isEmpty(utm)) {
      utilities.sessionStorageSave(config.utmStorage.key, utm);
    }

    if (document.referrer) {
      let documentReferrer = JSON.parse(JSON.stringify(document.referrer));
      utilities.sessionStorageSave(config.sessionVariables.referrerKey, documentReferrer);
    }
  }

  load() {
    if (Arrow.noAsyncAssets === true) {
      //Initialize app for third-party bundle
      mediator.trigger(config.events.global.appIsInitialising);
    } else {
      this._loadAsyncAssets(this._applyInitialisationItems);
    }
  }

  _applyInitialisationItems(translations, serverConfig, globalisationItems) {
    if (window.sessionStorage.getItem('scPublishVersion') !== Arrow.scPublishVersion) {
      window.sessionStorage.setItem('scPublishVersion', Arrow.scPublishVersion);
      for (var i = 0; i < window.sessionStorage.length; i++) {
        var sessionKey = window.sessionStorage.key(i);
        if (sessionKey && sessionKey.indexOf('/api/') > -1) {
          window.sessionStorage.removeItem(sessionKey);
        }
      }
    }

    initialisationItems.translations = translations;
    initialisationItems.config = serverConfig;
    initialisationItems.globalisationItems = globalisationItems;

    Arrow.globalisationItems = initialisationItems.globalisationItems;
    Arrow.translations = initialisationItems.translations;

    //set cookie expiry and domain defaults:
    cookies.defaults = { path: '/', domain: initialisationItems.config.cookies.domain };

    var currencyCookie = cookies.get(config.cookies.currency, {
      domain: initialisationItems.config.cookies.domain
    });
    if (currencyCookie) {
      // Get the currency and culture from the cookie
      currencyCookie = utilities.deserialize(currencyCookie);
      config.locale.currencyIsoCode = currencyCookie.isocode;
      config.locale.culture = currencyCookie.culture;
    }

    //Extend our config with settings from server environment. $ has deep extend.
    config = $.extend(true, config, initialisationItems.config);
    mediator.set(config.events.global.appIsInitialising, true);
    mediator.trigger(config.events.global.appIsInitialising);
    let arrowFed = cookies.get(config.cookies.arrowFed);

    if (utilities.isEmptyArrowfed(arrowFed)) {
      cookies.remove(config.cookies.accessToken, { domain: initialisationItems.config.cookies.domain });
    }
  }

  _loadAsyncAssets(cb) {
    log.debug('Initialisation: Loading configs');
    var requestOptions = {
      data: utilities.addCustIpToDataObject({}),
      storageOptions: {
        enabled: true,
        expiryHandlerKey: EXPIRY_HANDLER_KEY.TRANSLATIONS
      },
      retryOptions: {
        enabled: true,
        attempts: 2
      }
    };
    initialisationItems.globalisationItems = [];
    initialisationItems.config = [];
    Promise.all([
      runRequest({ url: Arrow.translationsUrl, ...requestOptions }),
      runRequest({ url: Arrow.configUrl, ...requestOptions }),
      runRequest({ url: Arrow.globalisationUrl, ...requestOptions })
    ])
      .then((requests) => {
        cb(
          requests[0].response.translations,
          requests[1].response,
          requests[2].response
        );
      })
      .catch((error) => {
        this.showGlobalPageLoadingError();
        cb(
          { translations: {} },
          { cookies: { domain: config.cookies.defaultDomain } },
          null
        );
        log.error('Failed to load Initialisation Assets', error);
      });
  }

  showGlobalPageLoadingError() {
    //hard coding values because config may not be available because of errors
    Analytics.push('Error', 'Cart', 'Error Bar', 'Failed to load Initialisation Assets');
    document.getElementById('pageLoadingError').style.display = 'block';
  }

  extractUtm(query) {
    const params = utilities.deserialize(query);
    const results = {};
    Object.keys(params).forEach((k) => {
      if (k && (k.indexOf('utm_') === 0)) {
        results[k] = params[k];
      }
    });
    return results;
  }
}


// WEBPACK FOOTER //
// ./assets/utilities/Initialisation.js